import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { tokenStorage } from '@/api/client';
import { Loading } from '@/components/common/State';
import { useAuthStore } from '@/store/authStore';

interface Props {
  children: ReactNode;
  /** true 면 manager/admin 만 접근 가능 */
  requireManager?: boolean;
  /** true 면 admin 만 접근 가능 */
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireManager, requireAdmin }: Props) {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const restore = useAuthStore((s) => s.restore);
  const isManager = useAuthStore((s) => s.isManager);
  const isAdmin = useAuthStore((s) => s.isAdmin);

  // localStorage 에 남아 있는 user 는 토큰과 어긋날 수 있으므로 신뢰하지 않는다.
  // 토큰이 있으면 서버(/api/users/me)로 실제 권한을 확인한 뒤에 판정한다.
  useEffect(() => {
    if (!initialized) {
      void restore();
    }
  }, [initialized, restore]);

  const hasToken = !!tokenStorage.getAccessToken();

  if (!initialized && hasToken) {
    return <Loading label="로그인 정보를 확인하는 중입니다…" />;
  }

  if (!hasToken || !user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireManager && !isManager()) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
